import type { Period } from "@/shared/domain/metrics";

import { advancePeriodNs } from "./periodBounds";

/**
 * Synthetic placeholder representing a missing time bucket in an aggregated
 * series. Carries only the time range (Unix-epoch nanoseconds); no value
 * field. Use the `"value" in x` type guard at the use site to distinguish
 * real records from placeholders.
 */
export interface BucketPlaceholder {
  start_time: bigint;
  end_time: bigint;
}

/**
 * How many whole buckets are missing between `prev` and `curr`, measured in
 * `prev`'s own duration.
 *
 * Rounds to the nearest whole bucket: bucket durations vary slightly (e.g.
 * month lengths), so a truncating divide would mis-count. The ratio is a small
 * integer, so the `Number()` conversion is exact.
 *
 * Zero whenever `prev` has no positive duration. That case is not reachable
 * from a well-formed aggregate response, but it is the difference between a
 * wrong count and a non-terminating caller: dividing by a zero-width bucket
 * yields `Infinity`, and {@link fillMissingBuckets}'s loop would never finish.
 * Negative gaps (overlapping buckets) round to zero or below and are clamped
 * for the same reason.
 */
export function missingBucketCount(
  prev: BucketPlaceholder,
  curr: BucketPlaceholder,
): number {
  if (prev.end_time <= prev.start_time) {
    return 0;
  }
  const prevDurationNs = prev.end_time - prev.start_time;
  const gapNs = curr.start_time - prev.end_time;
  return Math.max(0, Math.round(Number(gapNs) / Number(prevDurationNs)));
}

/** Ceiling on placeholders synthesized into a single gap, so a malformed
 *  response degrades to a short axis rather than locking the tab up. Matches
 *  the ceiling `padAxisToDomain` puts on its own synthesis. */
const MAX_FILLED_BUCKETS = 1000;

/**
 * Insert synthetic placeholder entries for any missing time buckets between
 * consecutive real records.
 *
 * With a `period`, gaps are walked in that period's CALENDAR units — the
 * boundaries `date_trunc` puts the server's own buckets on, so a synthesized
 * slot sits exactly where a real one would have. Every caller that knows the
 * response's period should pass it: months are 28-31 days and years 365-366,
 * so the measured-width path below drifts off the calendar as soon as a gap
 * brackets February or a leap year.
 *
 * Without one, gap size is measured against each record's own duration. That
 * is exact for daily and weekly buckets, whose width is fixed, and it is the
 * best available answer for a response whose period is not to hand.
 *
 * All values are Unix-epoch nanoseconds.
 */
export function fillMissingBuckets<T extends BucketPlaceholder>(
  data: T[],
  period?: Period,
): (T | BucketPlaceholder)[] {
  if (data.length < 2) {
    return data;
  }

  // `start_time` is bigint; Array.sort needs a number comparator, and only the
  // sign matters — Number() of the difference preserves it (cf. computeTrend).
  const sorted = [...data].sort((a, b) => Number(a.start_time - b.start_time));

  const result: (T | BucketPlaceholder)[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const curr = sorted[i];

    if (period === undefined) {
      const prevDurationNs = prev.end_time - prev.start_time;
      const missingCount = missingBucketCount(prev, curr);
      for (let j = 0; j < missingCount; j++) {
        const bucketStartNs = prev.end_time + BigInt(j) * prevDurationNs;
        result.push({
          start_time: bucketStartNs,
          end_time: bucketStartNs + prevDurationNs,
        });
      }
    } else {
      let cursor = prev.end_time;
      let budget = MAX_FILLED_BUCKETS;
      while (budget > 0) {
        const next = advancePeriodNs(cursor, period);
        // `next > curr.start_time` stops short of a partial period: a slot
        // running past the real bucket it precedes would draw two points over
        // one span. `next <= cursor` cannot happen for a well-formed period,
        // but this loop is bounded on time rather than on a computed count.
        if (next <= cursor || next > curr.start_time) {
          break;
        }
        result.push({ start_time: cursor, end_time: next });
        cursor = next;
        budget -= 1;
      }
    }

    result.push(curr);
  }

  return result;
}
